import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../services/api'
import { useColors } from '../hooks/useColors'
import { useToast } from '../contexts/ToastContext'

type Violation = {
  violation_type: string
  order_id: string
  order_number: string
  state: string
  time_in_state_minutes: number
  sla_limit_minutes: number
  overdue_by_minutes: number
  assigned_agent?: string | null
}

export default function SlaMonitorPage() {
  const colors = useColors()
  const navigate = useNavigate()
  const { addToast } = useToast()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [summary, setSummary] = useState<Record<string, number>>({})
  const [violations, setViolations] = useState<Violation[]>([])

  const load = useCallback(async () => {
    try {
      setError('')
      const res = await adminApi.getSlaViolations()
      const payload = res?.data ?? res
      setSummary(payload?.summary || {})
      setViolations(Array.isArray(payload?.violations) ? payload.violations : [])
    } catch {
      setError('Unable to load SLA violations.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
    const id = window.setInterval(() => { void load() }, 30000)
    return () => window.clearInterval(id)
  }, [load])

  const cards = useMemo(() => ([
    { label: 'Placed > 5m', key: 'placed_over_5m' },
    { label: 'Searching Agent > 15m', key: 'searching_agent_over_15m' },
    { label: 'At Facility > 4h', key: 'at_facility_over_4h' },
    { label: 'Express At Risk', key: 'express_at_risk' },
  ]), [])

  const escalate = async (v: Violation) => {
    try {
      await adminApi.updateOrderNotes(v.order_id, `[SLA_ESCALATION] ${v.violation_type} overdue by ${v.overdue_by_minutes} min`)
      await load()
      addToast('Escalated to admin notes.', 'success')
    } catch {
      addToast('Failed to escalate.', 'error')
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <h2 className="font-display" style={{ margin: 0, color: colors.textPrimary }}>SLA Violation Monitor</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 12 }}>
        {cards.map((c) => (
          <div key={c.key} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
            <div style={{ color: colors.textMuted, fontSize: 12 }}>{c.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: colors.textPrimary }}>{summary[c.key] || 0}</div>
          </div>
        ))}
      </div>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
        {loading ? <div style={{ padding: 20, color: colors.textMuted }}>Loading SLA data...</div> : error ? <div style={{ padding: 20, color: '#b91c1c' }}>{error}</div> : violations.length === 0 ? <div style={{ padding: 20, color: colors.textMuted }}>No active SLA violations.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th>Order #</th><th>State</th><th>Time in State</th><th>SLA Limit</th><th>Overdue By</th><th>Assigned Agent</th><th>Actions</th></tr></thead>
            <tbody>
              {violations.map((v) => (
                <tr key={`${v.violation_type}-${v.order_id}`}>
                  <td>{v.order_number}</td>
                  <td>{v.state}</td>
                  <td>{v.time_in_state_minutes} min</td>
                  <td>{v.sla_limit_minutes > 0 ? `${v.sla_limit_minutes} min` : 'Risk Window'}</td>
                  <td style={{ color: '#b91c1c', fontWeight: 600 }}>{v.overdue_by_minutes} min</td>
                  <td>{v.assigned_agent || '-'}</td>
                  <td style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => navigate(`/orders?search=${encodeURIComponent(v.order_number)}`)}>Open</button>
                    <button onClick={() => void escalate(v)}>Escalate</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
